package dev.lingvo.site.pages.app.settings.providers

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.WorkspaceProviderSaveResultDto
import dev.lingvo.shared.ProviderReadiness
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.components.ProviderMark
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LKeyIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSecretField
import dev.lingvo.site.ds.LTrashIcon
import dev.lingvo.site.ds.LZapIcon
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.pages.app.settings.ProviderProtectedReadLeaf
import dev.lingvo.site.pages.app.settings.providerReadinessBadge
import dev.lingvo.site.pages.app.settings.providerReadinessWords
import dev.lingvo.site.pages.app.settings.rememberWorkspaceAuthorityState
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.runWorkspaceAction
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * One provider's credential, on its own route.
 *
 * **The key is write-only, and that is a property of the API rather than of this form.** The DTO has
 * no field that could carry a key — not a field the input disables, not a value the client is trusted
 * to hide. A masked hint like `sk-…4f2a` is enough to recognise which key is stored and useless to
 * anybody who reads it over a shoulder or out of a screenshot.
 *
 * Admins and owners only, enforced at the server: a provider key is spend, and a translator who
 * could read even its shape has more than they need.
 */
@Page("/app/settings/providers/{providerId}")
@Layout(".components.layouts.AppLayout")
@Composable
fun WorkspaceProviderPage() {
    val ctx = rememberPageContext()
    val providerId = ctx.route.params["providerId"].orEmpty()
    val workspaceContext = WorkspaceRequestContext.current()

    var refreshKey by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(0) }
    var key by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf("") }
    // **Which action is in flight, not whether one is.** A single page-level flag was handed to
    // all three credential buttons as their `busy`, so pressing "Save key" spun the spinner on
    // "Test stored key" and "Remove key" as well — the page read as three requests where there was
    // one, and nothing on it said which button the reader had actually pressed. The others stay
    // disabled for the length of the round trip, because a save racing a remove is a real race;
    // only the pressed one spins.
    var busyAction by rememberWorkspaceAuthorityState(workspaceContext) {
        mutableStateOf<ProviderCredentialActionId?>(null)
    }
    val busy = busyAction != null
    var message by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf<String?>(null) }
    var saved by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf<String?>(null) }

    val provider = MtProvider.entries.firstOrNull { it.id.equals(providerId, ignoreCase = true) }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            provider?.displayName ?: providerId,
            t("app.settings.providers.provider_id.one_credential_for_the_whole_workspace", "One credential for the whole workspace. Every project can use it unless it sets its own."),
            // The vendor's own colour, the same mark the workspace list draws, so arriving here
            // from a card of six confirms which one was opened without reading the heading.
            mark = provider?.let { { ProviderMark(it, size = 40) } },
        )

        ProviderProtectedReadLeaf(
            context = workspaceContext,
            refreshKey = refreshKey,
            available = provider != null,
            unavailable = {
                LAlert(
                    t("app.settings.providers.provider_id.no_such_provider", "No such provider"),
                    tf(
                "app.settings.providers.provider_id.provider_is_not_one_lingvo_supports",
                "'{provider}' is not one Lingvo supports.",
                "provider" to providerId,
            ),
                    variant = LAlertVariant.WARNING,
                )
            },
            providers = ApiClient::workspaceProviders,
        ) { providerClaim, providerReload ->
            val state = providerReload.state
            // Saving or testing a key reloads this page underneath the form. A reload that fails
            // says so beside the credential that is still on screen rather than replacing it.
            providerReload.refreshError?.let { failure ->
                LAlert(
                    t("app.settings.providers.provider_id.showing_the_last_answer_that_loaded", "Showing the last answer that loaded"),
                    failure.readable(),
                    variant = LAlertVariant.WARNING,
                    dense = true,
                    live = true,
                )
            }
            val activeProvider = provider ?: return@ProviderProtectedReadLeaf
            // Both are written by Save key and Remove key and by nothing else, so they are the
            // acknowledgement for a press that otherwise leaves a screen reader with silence.
            saved?.let {
                LAlert(
                    t("app.settings.providers.provider_id.saved", "Saved"),
                    it,
                    variant = LAlertVariant.SUCCESS,
                    live = true,
                )
            }
            message?.let {
                LAlert(
                    t("app.settings.providers.provider_id.that_didnt_work", "That didn't work"),
                    it,
                    variant = LAlertVariant.WARNING,
                    live = true,
                )
            }

            LAsyncContent(
                state,
                rows = 2,
                loadingLabel = t("app.settings.providers.provider_id.loading_the_provider", "Loading the provider"),
                onRetry = { refreshKey++ },
            ) { configured ->
                val current = configured.firstOrNull { it.provider == activeProvider }
                LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                        Row(Modifier.gap(Space.SM)) {
                            // One vocabulary with the workspace list and with every project's
                            // provider grid. This page used to answer the same question from
                            // `hasKey`, `keyUnreadable` and `testedOkAt` and call the answer
                            // "working", which is a fifth word for a four-state enum.
                            val readiness = current?.readiness ?: ProviderReadiness.NEEDS_SETUP
                            LBadge(
                                providerReadinessWords(readiness),
                                variant = providerReadinessBadge(readiness),
                            )
                            // The honest label for a key nobody has proved works. Otherwise
                            // somebody pastes a key with a trailing newline, sees a green Saved,
                            // and finds out three days later mid-pretranslate.
                            if (current?.hasKey == true && current.testedOkAt == null) {
                                LBadge(
                                    t("app.settings.providers.provider_id.stored_never_tested", "stored, never tested"),
                                    variant = LBadgeVariant.WARNING,
                                )
                            }
                            current?.keyHint?.let { hint ->
                                Span(Modifier.monoFont().fontSize(TypeScale.Small).toAttrs()) { Text(hint) }
                            }
                        }
                        current?.unavailableReason
                            ?.takeIf { current.readiness != ProviderReadiness.READY }
                            ?.let {
                                // The server's own sentence. It knows why this credential is not
                                // usable; the page does not, and guessing produced "no key" for a
                                // provider whose key was simply switched off.
                                LAlert(
                                    providerReadinessWords(current.readiness),
                                    it,
                                    variant = LAlertVariant.INFO,
                                    dense = true,
                                )
                            }

                        if (current?.keyUnreadable == true) {
                            LAlert(
                                t("app.settings.providers.provider_id.this_key_cannot_be_read", "This key cannot be read"),
                                t("app.settings.providers.provider_id.something_is_stored_but_it_will_2", "Something is stored but it will not decrypt — the encryption secret " +
                                    "changed, or a backup was restored without it. Machine translation " +
                                    "with this provider fails until the key is entered again. Nothing " +
                                    "else is affected."),
                                variant = LAlertVariant.DANGER,
                            )
                        }

                        LSecretField(
                            key,
                            { key = it },
                            Modifier.fillMaxWidth(),
                            label = if (current?.hasKey == true) {
                                t("app.settings.providers.provider_id.replace_the_key", "Replace the key")
                            } else {
                                t("app.settings.providers.provider_id.api_key", "API key")
                            },
                            // Never populated from the server, because the server does not
                            // send it: the DTO has no field that could carry a key.
                            placeholder = t("app.settings.providers.provider_id.paste_it_here", "Paste it here"),
                        )
                        LMuted(
                            t("app.settings.providers.provider_id.stored_encrypted_and_never_returned_by_2", "Stored encrypted and never returned by the API — not by this page, not by " +
                                "any endpoint. If you lose it, you replace it rather than read it back."),
                        )

                        Row(Modifier.gap(Space.SM)) {
                            ProviderCredentialAction(
                                pageClaim = providerClaim,
                                label = t("app.settings.providers.provider_id.save_key", "Save key"),
                                providerId = activeProvider.id,
                                apiKey = key,
                                enableProvider = true,
                                enabled = key.isNotBlank() && !busy,
                                busy = busyAction == ProviderCredentialActionId.SAVE,
                                setBusy = { running ->
                                    busyAction = if (running) ProviderCredentialActionId.SAVE else null
                                },
                                leadingIcon = { LKeyIcon() },
                                onSuccess = {
                                    key = ""
                                    refreshKey++
                                    saved = tr(
                                        "app.settings.providers.provider_id.saved_test_it_before_relying_on",
                                        "Saved. Test it before relying on it.",
                                    )
                                    message = null
                                },
                                onFailure = { error ->
                                    message = error.message?.takeIf(String::isNotBlank)
                                        ?: tr(
                                            "app.settings.providers.provider_id.couldnt_save_this_provider_key_retry",
                                            "Couldn't save this provider key. Retry?",
                                        )
                                },
                            )
                            if (current?.hasKey == true && !current.keyUnreadable) {
                                // **Testing a stored key without replacing it.** The only way to
                                // find out whether the key in the database still works was to paste
                                // it in again and press Save — which is impossible for a key nobody
                                // kept, and which rotates nothing while pretending to. A PUT with
                                // no `apiKey` leaves the stored value alone and runs the same probe
                                // Save runs, so the badge above this row becomes true again.
                                ProviderCredentialAction(
                                    pageClaim = providerClaim,
                                    label = t("app.settings.providers.provider_id.test_stored_key", "Test stored key"),
                                    providerId = activeProvider.id,
                                    apiKey = null,
                                    enableProvider = null,
                                    enabled = !busy,
                                    busy = busyAction == ProviderCredentialActionId.TEST,
                                    setBusy = { running ->
                                        busyAction = if (running) ProviderCredentialActionId.TEST else null
                                    },
                                    variant = LButtonVariant.SECONDARY,
                                    leadingIcon = { LZapIcon() },
                                    onSuccess = { result ->
                                        refreshKey++
                                        message = null
                                        saved = when (result?.testPassed) {
                                            true -> tr(
                                                "app.settings.providers.provider_id.the_stored_key_works",
                                                "The stored key works.",
                                            )
                                            false -> trf(
                                                "app.settings.providers.provider_id.the_provider_refused_the_stored_key",
                                                "The provider refused the stored key: {reason}",
                                                "reason" to result.testMessage.orEmpty(),
                                            )
                                            else -> tr(
                                                "app.settings.providers.provider_id.the_provider_gave_no_answer",
                                                "The provider gave no answer, so the key is still unproven.",
                                            )
                                        }
                                    },
                                    onFailure = { error ->
                                        message = error.message?.takeIf(String::isNotBlank)
                                            ?: tr(
                                                "app.settings.providers.provider_id.couldnt_test_this_provider_key_retry",
                                                "Couldn't test this provider key. Retry?",
                                            )
                                    },
                                )
                            }
                            if (current?.hasKey == true) {
                                ProviderCredentialAction(
                                    pageClaim = providerClaim,
                                    label = t("app.settings.providers.provider_id.remove_key", "Remove key"),
                                    providerId = activeProvider.id,
                                    apiKey = "",
                                    enableProvider = false,
                                    enabled = !busy,
                                    busy = busyAction == ProviderCredentialActionId.REMOVE,
                                    setBusy = { running ->
                                        busyAction = if (running) ProviderCredentialActionId.REMOVE else null
                                    },
                                    variant = LButtonVariant.GHOST,
                                    leadingIcon = { LTrashIcon() },
                                    // The narrower action — clearing a *project* override — already
                                    // asks first. This one destroys a secret the API will never hand
                                    // back, for every project in the workspace, and fired on one
                                    // click.
                                    confirm = ProviderCredentialConfirmation(
                                        title = tf(
                                            "app.settings.providers.provider_id.remove_key_title",
                                            "Remove the {provider} key?",
                                            "provider" to activeProvider.displayName,
                                        ),
                                        confirmLabel = t("app.settings.providers.provider_id.remove_key", "Remove key"),
                                        consequenceTitle = t("app.settings.providers.provider_id.this_cannot_be_undone", "This cannot be undone"),
                                        consequence = tf(
                                            "app.settings.providers.provider_id.every_project_that_uses_this_workspace",
                                            "Every project that uses this workspace credential " +
                                                "stops translating with {provider} until a new key " +
                                                "is stored. Lingvo never returns a stored key, so " +
                                                "this one cannot be read back — you would have to " +
                                                "get a new one from the provider.",
                                            "provider" to activeProvider.displayName,
                                        ),
                                    ),
                                    onSuccess = {
                                        refreshKey++
                                        saved = tr("app.settings.providers.provider_id.removed", "Removed.")
                                        message = null
                                    },
                                    onFailure = { error ->
                                        message = error.message?.takeIf(String::isNotBlank)
                                            ?: tr(
                                                "app.settings.providers.provider_id.couldnt_remove_this_provider_key_retry",
                                                "Couldn't remove this provider key. Retry?",
                                            )
                                    },
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

/** The three credential actions this page offers, so "busy" can name one of them. */
internal enum class ProviderCredentialActionId { SAVE, TEST, REMOVE }

/**
 * What a destructive credential action says before it fires. Absent for `Save key`, which replaces
 * a value rather than destroying one.
 */
internal data class ProviderCredentialConfirmation(
    val title: String,
    val confirmLabel: String,
    val consequenceTitle: String,
    val consequence: String,
)

@Composable
internal fun ProviderCredentialAction(
    pageClaim: WorkspaceActionClaim,
    label: String,
    providerId: String,
    /**
     * The replacement key, `""` to clear the stored one, or `null` to leave it exactly as it is.
     *
     * Null is what makes "Test stored key" possible: the route runs its probe on every PUT, so a
     * body that carries neither a key nor an enabled flag is a test and nothing else.
     */
    apiKey: String?,
    enableProvider: Boolean?,
    enabled: Boolean,
    busy: Boolean,
    setBusy: (Boolean) -> Unit,
    variant: LButtonVariant = LButtonVariant.PRIMARY,
    leadingIcon: (@Composable () -> Unit)? = null,
    confirm: ProviderCredentialConfirmation? = null,
    currentContext: () -> WorkspaceRequestContext? = WorkspaceRequestContext::current,
    save: suspend (String, String, String?, Boolean?) -> WorkspaceProviderSaveResultDto? =
        { workspaceSlug, id, replacement, active ->
            ApiClient.saveWorkspaceProvider(workspaceSlug, id, replacement, active)
        },
    launchAction: ((suspend () -> Unit) -> Unit)? = null,
    onSuccess: (WorkspaceProviderSaveResultDto?) -> Unit,
    onFailure: (Throwable) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val launch: (suspend () -> Unit) -> Unit = launchAction ?: { action ->
        scope.launch { action() }
    }
    var confirming by remember { mutableStateOf(false) }

    val fire: () -> Unit = {
        val actionClaim = pageClaim.takeIf { it.remainsAuthorized(currentContext()) }
        if (actionClaim != null) {
            launch {
                runWorkspaceAction(
                    claim = actionClaim,
                    currentContext = currentContext,
                    setBusy = setBusy,
                    transport = { workspaceSlug ->
                        save(workspaceSlug, providerId, apiKey, enableProvider)
                    },
                    onSuccess = { result -> onSuccess(result) },
                    onFailure = onFailure,
                )
            }
        }
    }

    LButton(
        label,
        {
            if (confirm == null) {
                fire()
            } else {
                confirming = true
            }
        },
        enabled = enabled,
        loading = busy,
        variant = variant,
        leadingIcon = leadingIcon,
    )
    confirm?.let { spec ->
        LConfirmDialog(
            open = confirming,
            title = spec.title,
            confirmLabel = spec.confirmLabel,
            onConfirm = {
                confirming = false
                fire()
            },
            onCancel = { confirming = false },
            consequenceTitle = spec.consequenceTitle,
            consequence = spec.consequence,
            busy = busy,
        )
    }
}

